// components/ExecutionPlanModule.js
import React from "react"
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card"
import { Button } from "../../ui/button"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../ui/tabs"
import { Avatar, AvatarFallback, AvatarImage } from "../../ui/avatar"
import { ThumbsUp, ThumbsDown, MessageSquare, Edit } from 'lucide-react'
import { Textarea } from "../../ui/textarea"
import { Label } from "../../ui/label"
import useExecutionPlan from "../../../hooks/useExecutionPlan"

const ExecutionPlanModule = ({ actionId, action }) => {
  const {
    executionPlan,
    suggestions,
    comments,
    newSuggestion,
    newComment,
    activeTab,
    loading,
    setActiveTab,
    setNewComment,
    updateNewSuggestion,
    handleSubmitSuggestion,
    handleSubmitComment,
    handleVote
  } = useExecutionPlan(actionId)

  if (loading) {
    return <div className="p-4">Loading execution plan data...</div>
  }

  return (
    <div>
      <CardHeader className="px-0 pt-0">
        <CardTitle>Execution Plan</CardTitle>
      </CardHeader>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="current">Current</TabsTrigger>
          <TabsTrigger value="suggestions">Suggestions ({suggestions.length})</TabsTrigger>
          <TabsTrigger value="discussion">Discussion ({comments.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="current">
          <Card>
            <CardContent className="p-4">
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium mb-2">Overview</h3>
                  <div className="prose max-w-none">
                    {executionPlan.overview ? (
                      <p>{executionPlan.overview}</p>
                    ) : (
                      <p className="text-muted-foreground">No execution plan overview has been defined yet.</p>
                    )}
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-medium mb-2">Execution Steps</h3>
                  {executionPlan.steps && executionPlan.steps.length > 0 ? (
                    <ol className="list-decimal pl-5 space-y-2">
                      {executionPlan.steps.map((step, index) => (
                        <li key={index}>{step}</li>
                      ))}
                    </ol>
                  ) : (
                    <p className="text-muted-foreground">No execution steps have been defined yet.</p>
                  )}
                </div>

                <div>
                  <h3 className="text-lg font-medium mb-2">Risks & Mitigation</h3>
                  <div className="prose max-w-none">
                    {executionPlan.risks ? (
                      <p>{executionPlan.risks}</p>
                    ) : (
                      <p className="text-muted-foreground">No risks or mitigation strategies have been defined yet.</p>
                    )}
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-medium mb-2">Success Criteria</h3>
                  <div className="prose max-w-none">
                    {executionPlan.successCriteria ? (
                      <p>{executionPlan.successCriteria}</p>
                    ) : (
                      <p className="text-muted-foreground">No success criteria have been defined yet.</p>
                    )}
                  </div>
                </div>

                <div className="mt-4 flex justify-end">
                  <Button onClick={() => setActiveTab("suggestions")} variant="outline">
                    <Edit className="mr-2 h-4 w-4" /> Suggest Edit
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="suggestions">
          <Card className="mb-4">
            <CardContent className="p-4">
              <h3 className="text-lg font-medium mb-4">Suggest an Execution Plan Update</h3>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="overview">Overview</Label>
                  <Textarea
                    id="overview"
                    value={newSuggestion.overview}
                    onChange={(e) => updateNewSuggestion("overview", e.target.value)}
                    placeholder="Provide a high-level overview of the execution plan..."
                    className="min-h-[100px]"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="steps">Execution Steps (one per line)</Label>
                  <Textarea
                    id="steps"
                    value={newSuggestion.steps}
                    onChange={(e) => updateNewSuggestion("steps", e.target.value)}
                    placeholder="List the steps required to execute this action, one per line..."
                    className="min-h-[150px]"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="risks">Risks & Mitigation</Label>
                  <Textarea
                    id="risks"
                    value={newSuggestion.risks}
                    onChange={(e) => updateNewSuggestion("risks", e.target.value)}
                    placeholder="Describe potential risks and mitigation strategies..."
                    className="min-h-[100px]"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="success-criteria">Success Criteria</Label>
                  <Textarea
                    id="success-criteria"
                    value={newSuggestion.successCriteria}
                    onChange={(e) => updateNewSuggestion("successCriteria", e.target.value)}
                    placeholder="Define how success will be measured for this action..."
                    className="min-h-[100px]"
                  />
                </div>

                <div className="flex justify-end">
                  <Button onClick={handleSubmitSuggestion}>Submit Execution Plan</Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {suggestions.length === 0 ? (
            <p className="text-muted-foreground text-center py-4">
              No execution plan suggestions yet. Be the first to suggest a plan!
            </p>
          ) : (
            <div className="space-y-4">
              {suggestions.map((suggestion) => (
                <Card key={suggestion.id}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <Avatar>
                        <AvatarImage src={suggestion.user?.avatarUrl || "/placeholder.svg"} />
                        <AvatarFallback>{suggestion.user?.name?.substring(0, 2) || "U"}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <div className="flex justify-between items-center mb-1">
                          <span className="font-medium">{suggestion.user?.name || "Anonymous"}</span>
                          <span className="text-xs text-muted-foreground">
                            {new Date(suggestion.createdAt).toLocaleString()}
                          </span>
                        </div>

                        <div className="space-y-3 mb-4">
                          <div>
                            <h4 className="text-sm font-medium">Overview</h4>
                            <p className="text-sm mt-1">{suggestion.overview}</p>
                          </div>

                          {suggestion.steps && (
                            <div>
                              <h4 className="text-sm font-medium">Execution Steps</h4>
                              <ol className="list-decimal pl-5 text-sm mt-1">
                                {suggestion.steps
                                  .split("\n")
                                  .filter((step) => step.trim())
                                  .map((step, index) => (
                                    <li key={index}>{step}</li>
                                  ))}
                              </ol>
                            </div>
                          )}

                          {suggestion.risks && (
                            <div>
                              <h4 className="text-sm font-medium">Risks & Mitigation</h4>
                              <p className="text-sm mt-1">{suggestion.risks}</p>
                            </div>
                          )}

                          {suggestion.successCriteria && (
                            <div>
                              <h4 className="text-sm font-medium">Success Criteria</h4>
                              <p className="text-sm mt-1">{suggestion.successCriteria}</p>
                            </div>
                          )}
                        </div>

                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleVote(suggestion.id, "up")}
                            className={suggestion.userVote === "up" ? "bg-green-50" : ""}
                          >
                            <ThumbsUp className="h-4 w-4 mr-1" />
                            <span>{suggestion.upvotes || 0}</span>
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleVote(suggestion.id, "down")}
                            className={suggestion.userVote === "down" ? "bg-red-50" : ""}
                          >
                            <ThumbsDown className="h-4 w-4 mr-1" />
                            <span>{suggestion.downvotes || 0}</span>
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="discussion">
          <Card className="mb-4">
            <CardContent className="p-4">
              <div className="space-y-4 max-h-96 overflow-y-auto mb-4">
                {comments.length === 0 ? (
                  <p className="text-muted-foreground text-center py-4">No comments yet. Start the discussion!</p>
                ) : (
                  comments.map((comment) => (
                    <div key={comment.id} className="flex gap-3 p-3 rounded-lg bg-gray-50">
                      <Avatar>
                        <AvatarImage src={comment.user?.avatarUrl || "/placeholder.svg"} />
                        <AvatarFallback>{comment.user?.name?.substring(0, 2) || "U"}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <div className="flex justify-between items-center mb-1">
                          <span className="font-medium">{comment.user?.name || "Anonymous"}</span>
                          <span className="text-xs text-muted-foreground">
                            {new Date(comment.createdAt).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-sm">{comment.content}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="space-y-2">
                <Textarea
                  placeholder="Add to the discussion..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  className="min-h-[100px]"
                />
                <div className="flex justify-end">
                  <Button onClick={handleSubmitComment}>
                    <MessageSquare className="mr-2 h-4 w-4" /> Post Comment
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}

export default ExecutionPlanModule